import { useCallback, useEffect } from 'react';

import { useScrollCapability } from '@embedpdf/plugin-scroll/react';

export default function PDFSearchBar({
  documentId,
  query,
  onQueryChange,
  results,
  resultIndex,
  searching,
  onSearch,
  onNavigate,
}) {
  const { provides: scroll } = useScrollCapability(documentId);

  const scrollToResult = useCallback(
    (idx) => {
      if (!results.length || !scroll) return;
      scroll.scrollToPage({ pageNumber: results[idx].pageIndex + 1, behavior: 'smooth' });
    },
    [results, scroll],
  );

  useEffect(() => {
    if (results.length > 0) scrollToResult(resultIndex);
  }, [resultIndex, results, scrollToResult]);

  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();
      onSearch(query);
    },
    [query, onSearch],
  );

  const navigate = useCallback(
    (direction) => {
      onNavigate(direction);
    },
    [onNavigate],
  );

  return (
    <form
      onSubmit={handleSearch}
      className="d-flex gap-2 align-items-center"
      style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-border)' }}
    >
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="Search document..."
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        style={{ maxWidth: 260 }}
      />
      <button
        type="submit"
        className="btn btn-sm btn-primary"
        disabled={searching || !query.trim()}
      >
        {searching ? 'Searching…' : 'Search'}
      </button>

      {results.length > 0 && (
        <>
          <span style={{ fontSize: 12, color: 'var(--color-text)', whiteSpace: 'nowrap' }}>
            {resultIndex + 1} / {results.length}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate(-1)}
            aria-label="Previous result"
          >
            ‹
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate(1)}
            aria-label="Next result"
          >
            ›
          </button>
        </>
      )}

      {!searching && query && results.length === 0 && (
        <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.6 }}>No results</span>
      )}
    </form>
  );
}
